import { useState } from 'react';
import { AlertCircle, User, Mail, Building2, Globe2, Lock, CheckCircle, ArrowRight, ArrowLeft, UserPlus, Eye, EyeOff } from 'lucide-react';
import Reveal from '../components/animations/Reveal';
import AuthBackground from '../components/animations/AuthBackground';
import { registerUser, saveAuthSession } from '../api/authApi';
import COUNTRIES from '../constants/countries';

const STRENGTH_LEVELS = [
  { label: 'Weak', color: 'bg-red-400' },
  { label: 'Fair', color: 'bg-orange-400' },
  { label: 'Good', color: 'bg-yellow-400' },
  { label: 'Strong', color: 'bg-emerald-400' },
  { label: 'Very strong', color: 'bg-emerald-500' },
];

const getPasswordStrength = (pwd) => {
  if (!pwd) return { score: 0, label: '', color: 'bg-white/20' };
  let score = 0;
  if (pwd.length >= 8) score++;
  if (pwd.length >= 12) score++;
  if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  return { score, ...STRENGTH_LEVELS[Math.min(score - 1, STRENGTH_LEVELS.length - 1)] };
};

const INPUT_CLASS =
  'input-anim w-full bg-white/95 border border-white/40 rounded-xl p-3 pl-11 text-slate-800 placeholder-slate-400 focus:border-brand focus:ring-2 focus:ring-brand/30 focus:outline-none transition-all';

const RegisterPage = ({ setActiveTab }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    institution: '',
    department:'',
    country: '',
  });
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const updateField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const strength = getPasswordStrength(form.password);

  const validateStep1 = () => {
    if (!form.name.trim()) return 'Full name is required';
    if (!form.email.trim()) return 'Email is required';
    if (!form.institution.trim()) return 'Institution is required';
    if (!form.department.trim()) return 'Department is required';
    if (!form.country) return 'Please select your country';
    return null;
  };

  const validateStep2 = () => {
    if (form.password.length < 8) return 'Password must be at least 8 characters';
    if (form.password !== form.confirmPassword) return 'Passwords do not match';
    return null;
  };

  const handleNext = (e) => {
    e.preventDefault();
    const validationError = validateStep1();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setStep(2);
  };

  const handleBack = () => {
    setError(null);
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateStep2();
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setIsSubmitting(true);
    try {
      const { token, user } = await registerUser({
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        institution: form.institution.trim(),
        department: form.department.trim(),
        country: form.country,
      });
      saveAuthSession({ token, user });
      setActiveTab?.('analysis');
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-[calc(100vh-4rem)] flex flex-col items-center pt-16 pb-16 px-4">
      <AuthBackground />
      <div className="relative max-w-md w-full mx-auto sm:px-6 lg:px-8">
        <Reveal>
          <div className="text-center mb-5">
            <h2 className="font-display text-4xl font-bold text-white mb-2 drop-shadow-sm">Register</h2>
            <p className="text-xl text-white/80">Create your CryMiner account</p>
          </div>
        </Reveal>

        {/* Stepper */}
        <Reveal delay={60}>
          <div className="flex items-center justify-center gap-3 mb-4">
            {[1, 2].map((s) => (
              <div key={s} className="flex items-center gap-3">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ${
                    step > s
                      ? 'bg-brand text-white'
                      : step === s
                      ? 'bg-brand text-white scale-110 shadow-lg shadow-brand/40'
                      : 'bg-white/10 text-white/40 border border-white/20'
                  }`}
                >
                  {step > s ? <CheckCircle className="w-4 h-4" /> : s}
                </div>
                {s === 1 && (
                  <div className={`w-14 h-0.5 rounded-full transition-all duration-300 ${step > 1 ? 'bg-brand' : 'bg-white/20'}`} />
                )}
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-white/50 mb-5">
            Step {step} of 2 — {step === 1 ? 'Your details' : 'Secure your account'}
          </p>
        </Reveal>

        <Reveal delay={100}>
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl">
            {step === 1 ? (
              <form className="space-y-5" onSubmit={handleNext}>
                <div className="relative">
                  <User className="w-4.5 h-4.5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={updateField('name')}
                    className={INPUT_CLASS}
                    placeholder="Full name"
                  />
                </div>

                <div className="relative">
                  <Mail className="w-4.5 h-4.5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={updateField('email')}
                    className={INPUT_CLASS}
                    placeholder="Email address"
                  />
                </div>

                <div className="relative">
                  <Building2 className="w-4.5 h-4.5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    required
                    value={form.institution}
                    onChange={updateField('institution')}
                    className={INPUT_CLASS}
                    placeholder="Institution or organization"
                  />
                </div>

                <div className="relative">
                  <Building2 className="w-4.5 h-4.5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    required
                    value={form.department}
                    onChange={updateField('department')}
                    className={INPUT_CLASS}
                    placeholder="Department"
                  />
                </div>
                <div className="relative">
                  <Globe2 className="w-4.5 h-4.5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none z-10" />
                  <select
                    required
                    value={form.country}
                    onChange={updateField('country')}
                    className={`${INPUT_CLASS} ${form.country ? 'text-slate-800' : 'text-slate-400'}`}
                  >
                    <option value="" disabled>Select your country</option>
                    {COUNTRIES.map((c) => (
                      <option key={c} value={c} className="text-slate-800">{c}</option>
                    ))}
                  </select>
                </div>

                {error && (
                  <div className="flex items-start space-x-3 bg-red-500/10 border border-red-400/30 rounded-2xl p-4">
                    <AlertCircle className="w-5 h-5 text-red-300 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-200">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  className="brand-btn w-full px-6 py-3 rounded-xl font-semibold hover:scale-[1.02] transition-transform duration-300 flex items-center justify-center gap-2"
                >
                  Continue
                  <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            ) : (
              <form className="space-y-5" onSubmit={handleSubmit}>
                <div>
                  <div className="relative">
                    <Lock className="w-4.5 h-4.5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={form.password}
                      onChange={updateField('password')}
                      className={`${INPUT_CLASS} pr-11`}
                      placeholder="Create a password (min. 8 characters)"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      tabIndex={-1}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="w-4.5 h-4.5" /> : <Eye className="w-4.5 h-4.5" />}
                    </button>
                  </div>
                  {form.password && (
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex gap-1 flex-1">
                        {STRENGTH_LEVELS.map((_, i) => (
                          <div
                            key={i}
                            className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
                              i < strength.score ? strength.color : 'bg-white/15'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-xs text-white/60 w-20 text-right">{strength.label}</span>
                    </div>
                  )}
                </div>

                <div className="relative">
                  <Lock className="w-4.5 h-4.5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    value={form.confirmPassword}
                    onChange={updateField('confirmPassword')}
                    className={`${INPUT_CLASS} pr-11`}
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((s) => !s)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    tabIndex={-1}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? <EyeOff className="w-4.5 h-4.5" /> : <Eye className="w-4.5 h-4.5" />}
                  </button>
                </div>

                {error && (
                  <div className="flex items-start space-x-3 bg-red-500/10 border border-red-400/30 rounded-2xl p-4">
                    <AlertCircle className="w-5 h-5 text-red-300 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-200">{error}</p>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="px-5 py-3 rounded-xl font-semibold text-white/80 border border-white/20 hover:bg-white/10 transition-colors flex items-center gap-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="brand-btn flex-1 px-6 py-3 rounded-xl font-semibold hover:scale-[1.02] transition-transform duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      'Creating account...'
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4" />
                        Create Account
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export default RegisterPage;